import React, { ReactElement } from 'react';
import { Params, RouteProps } from 'react-router-dom';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { NestedKeys } from 'record-it-utils/utils/utilityTypes/NestedKeys';
import { AppEntityManager } from '../classes/entities/AppEntityManager';
import { UserPermissions } from '../classes/misc/UserPermissions/UserPermissions';
import { RoutePaths } from '../classes/RoutePaths/RoutePaths';
import { ConstructionSiteAdminCreateConstructionSitePage } from '../components/page/ConstructionSiteAdminCreateConstructionSitePage/ConstructionSiteAdminCreateConstructionSitePage';
import { ConstructionSiteAdminPage } from '../components/page/ConstructionSiteAdminPage/ConstructionSiteAdminPage';
import { CreateWorkTimeEntryCreateConstructionSitePage } from '../components/page/CreateWorkTimeEntryCreateConstructionSitePage/CreateWorkTimeEntryCreateConstructionSitePage';
import { CreateWorkTimeEntryPage } from '../components/page/CreateWorkTimeEntryPage/CreateWorkTimeEntryPage';
import { CreateWorkTimeEntrySelectActivityPage } from '../components/page/CreateWorkTimeEntrySelectActivityPage/CreateWorkTimeEntrySelectActivityPage';
import { CreateWorkTimeEntrySelectConstructionSitePage } from '../components/page/CreateWorkTimeEntrySelectConstructionSitePage/CreateWorkTimeEntrySelectConstructionSitePage';
import { EditWorkTimeEntryPage } from '../components/page/EditWorkTimeEntryPage/EditWorkTimeEntryPage';
import { ForgotPasswordPage } from '../components/page/ForgotPasswordPage/ForgotPasswordPage';
import { HomePage } from '../components/page/HomePage/HomePage';
import { LoginPage } from '../components/page/LoginPage/LoginPage';
import { MyWorkTimeOverviewPage } from '../components/page/MyWorkTimeOverviewPage/MyWorkTimeOverviewPage';
import { ResetPasswordPage } from '../components/page/ResetPasswordPage/ResetPasswordPage';
import { TimeTrackingActivityAdminPage } from '../components/page/TimeTrackingActivityAdminPage/TimeTrackingActivityAdminPage';
import { TimeTrackingActivityCompilationAdminPage } from '../components/page/TimeTrackingActivityCompilationAdminPage/TimeTrackingActivityCompilationAdminPage';
import { TimeTrackingConfigurationAdminPage } from '../components/page/TimeTrackingConfigurationAdminPage/TimeTrackingConfigurationAdminPage';
import { UserAdminPage } from '../components/page/UserAdminPage/UserAdminPage';
import { UserGroupAdminPage } from '../components/page/UserGroupAdminPage/UserGroupAdminPage';
import { WorkTimeAdminPage } from '../components/page/WorkTimeAdminPage/WorkTimeAdminPage';
import { de } from '../translation/de';
export enum AuthenticatedPermission {
  OnlyAuthenticated = 'onlyAuthenticated',
  OnlyUnauthenticated = 'onlyUnauthenticated',
  All = 'all',
}
export enum LayoutType {
  Default = 'default',
  Login = 'login',
}
const navigationConfigurationsWithoutKey: Array<NavigationConfiguration> = [{
  titleTk: 'App.navigationConfigurations.pageTitle.login',
  layoutType: LayoutType.Login,
  routeProps: {
    path: RoutePaths.loginPage(),
    element: <LoginPage></LoginPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyUnauthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.forgotPassword',
  layoutType: LayoutType.Login,
  routeProps: {
    path: RoutePaths.forgotPassword(),
    element: <ForgotPasswordPage></ForgotPasswordPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyUnauthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.resetPassword',
  layoutType: LayoutType.Login,
  routeProps: {
    path: RoutePaths.resetPassword(':token'),
    element: <ResetPasswordPage></ResetPasswordPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyUnauthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.home',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.home(),
    element: <HomePage></HomePage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.createWorkTimeEntry',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.createWorkTimeEntry(),
    element: <CreateWorkTimeEntryPage></CreateWorkTimeEntryPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated
  }
}, createCreateWorkTimeEntrySelectActivityPageConfiguration(RoutePaths.createWorkTimeEntrySelectActivity()), createCreateWorkTimeEntrySelectActivityPageConfiguration(RoutePaths.createWorkTimeEntrySelectActivity(':parentActivityId')), {
  titleTk: 'App.navigationConfigurations.pageTitle.createWorkTimeEntrySelectConstructionSite',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.createWorkTimeEntrySelectConstructionSite(),
    element: <CreateWorkTimeEntrySelectConstructionSitePage></CreateWorkTimeEntrySelectConstructionSitePage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.createWorkTimeEntryCreateConstructionSite',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.createWorkTimeEntryCreateConstructionSite(),
    element: <CreateWorkTimeEntryCreateConstructionSitePage></CreateWorkTimeEntryCreateConstructionSitePage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditConstructionSites()
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.trackedWorkTimeOverview',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.myWorkTimeOverview(),
    element: <MyWorkTimeOverviewPage></MyWorkTimeOverviewPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.trackedWorkTimeAdmin',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminWorkTime(':userGroupId', ':userId'),
    element: <WorkTimeAdminPage></WorkTimeAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions,
      params
    }) => {
      const {
        userGroupId
      } = params;
      assertNotNullOrUndefined(userGroupId, 'no userGroupId found in params');
      return userPermissions.canEditWorkTimeEntriesOfUserGroup(userGroupId);
    }
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.trackedWorkTimeEdit',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.editWorkTimeEntry(':workTimeEntryId'),
    element: <EditWorkTimeEntryPage></EditWorkTimeEntryPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.administrationTimeTrackingConfigurations',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminTimeTrackingConfigurations(),
    element: <TimeTrackingConfigurationAdminPage></TimeTrackingConfigurationAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditTimeTrackingConfigurations()
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.administrationTimeTrackingActivityCompilations',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminTimeTrackingActivityCompilations(':userGroupId'),
    element: <TimeTrackingActivityCompilationAdminPage></TimeTrackingActivityCompilationAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      params,
      userPermissions
    }) => {
      const {
        userGroupId
      } = params;
      assertNotNullOrUndefined(userGroupId, 'no userGroupId found in params');
      return userPermissions.canEditTimeTrackingActivityCompilationsOfUserGroup(userGroupId);
    }
  }
}, createTimeTrackingActivityAdminPageConfiguration(RoutePaths.adminTimeTrackingActivities(':timeTrackingActivityCompilationId')), createTimeTrackingActivityAdminPageConfiguration(RoutePaths.adminTimeTrackingActivities(':timeTrackingActivityCompilationId', ':parentActivityId')), {
  titleTk: 'App.navigationConfigurations.pageTitle.constructionSiteOverview',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminConstructionSites(),
    element: <ConstructionSiteAdminPage></ConstructionSiteAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditConstructionSites()
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.createConstructionSite',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminConstructionSitesCreateConstructionSite(':userGroupId'),
    element: <ConstructionSiteAdminCreateConstructionSitePage></ConstructionSiteAdminCreateConstructionSitePage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditConstructionSites()
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.administrationUsers',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminUsers(),
    element: <UserAdminPage></UserAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditUsers()
  }
}, {
  titleTk: 'App.navigationConfigurations.pageTitle.administrationUsergroups',
  layoutType: LayoutType.Default,
  routeProps: {
    path: RoutePaths.adminUserGroups(),
    element: <UserGroupAdminPage></UserGroupAdminPage>
  },
  permissions: {
    authenticated: AuthenticatedPermission.OnlyAuthenticated,
    checkUserPermissions: ({
      userPermissions
    }) => userPermissions.canEditUserGroups()
  }
}];
export const navigationConfigurations = navigationConfigurationsWithoutKey.map((config, index) => {
  return {
    key: `${index} - ${config.routeProps.path}`,
    ...config
  };
});
type NavigationConfiguration = {
  routeProps: {
    path: string;
    element: ReactElement;
  } & Omit<RouteProps, 'path' | 'element' | 'lazy' | 'index'>;
  titleTk: NestedKeys<typeof de>;
  layoutType: LayoutType;
  permissions: NavigationConfigurationPermissions;
};
export type NavigationConfigurationWithKey = NavigationConfiguration & {
  key: string;
};
export type NavigationConfigurationPermissions = {
  authenticated: AuthenticatedPermission.OnlyUnauthenticated | AuthenticatedPermission.All;
} | {
  authenticated: AuthenticatedPermission.OnlyAuthenticated;
  checkUserPermissions?: (options: {
    userPermissions: UserPermissions;
    params: Readonly<Params<string>>;
    entityManager: AppEntityManager;
  }) => boolean;
};
function createCreateWorkTimeEntrySelectActivityPageConfiguration(path: string): NavigationConfiguration {
  return {
    titleTk: 'App.navigationConfigurations.pageTitle.createWorkTimeEntrySelectActivity',
    layoutType: LayoutType.Default,
    routeProps: {
      path,
      element: <CreateWorkTimeEntrySelectActivityPage></CreateWorkTimeEntrySelectActivityPage>
    },
    permissions: {
      authenticated: AuthenticatedPermission.OnlyAuthenticated
    }
  };
}
function createTimeTrackingActivityAdminPageConfiguration(path: string): NavigationConfiguration {
  return {
    titleTk: 'App.navigationConfigurations.pageTitle.administrationTimeTrackingActivities',
    layoutType: LayoutType.Default,
    routeProps: {
      path,
      element: <TimeTrackingActivityAdminPage></TimeTrackingActivityAdminPage>
    },
    permissions: {
      authenticated: AuthenticatedPermission.OnlyAuthenticated,
      checkUserPermissions: ({
        params,
        userPermissions,
        entityManager
      }) => {
        const {
          timeTrackingActivityCompilationId
        } = params;
        assertNotNullOrUndefined(timeTrackingActivityCompilationId, 'no timeTrackingActivityCompilationId found in params');
        const compilation = entityManager.entityRepositoryContainer.getByEntityName(EntityName.TimeTrackingActivityCompilation).getRequiredById(timeTrackingActivityCompilationId);
        return userPermissions.canEditTimeTrackingActivitiesOfUserGroup(compilation.ownerUserGroupId);
      }
    }
  };
}